export interface NamedRecord {
  id: string;
  name: string;
}

export interface PreferenceOption {
  label: string;
  value: string;
}

export const ACCOUNT_DEFAULT_VALUE = "";

// Mirrors the retired profile form: the choices are the account's active
// records plus the user's current one even if it has since been archived, and
// the select only appears when there is more than one to choose between.
export const buildPreferenceOptions = (
  active: NamedRecord[],
  current: NamedRecord | null | undefined
): { visible: boolean; options: PreferenceOption[] } => {
  const records = [...active];
  if (current && !records.some((r) => r.id === current.id)) {
    records.push(current);
  }

  return {
    visible: records.length > 1,
    options: [
      { label: "Use account default", value: ACCOUNT_DEFAULT_VALUE },
      ...records
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((r) => ({ label: r.name, value: r.id })),
    ],
  };
};

// JSON:API to-one linkage for the user's preference; null restores the
// account default. The generated V2Relationships type has no nullable data.
export const preferenceRelationship = (
  id: string | null | undefined,
  type: "rate-tables" | "currencies"
) => ({ data: id ? { id: String(id), type } : null } as any);

export interface UserPreferences {
  rateTableId: string | null;
  currencyId: string | null;
  currentRateTable: NamedRecord | null;
  currentCurrency: NamedRecord | null;
}

// Reads the preferences off a user document fetched with
// include=rate-table,currency; the included records supply names for
// selections that are no longer active.
export const readUserPreferences = (doc: any): UserPreferences => {
  const linked = (key: string, type: string): NamedRecord | null => {
    const id = doc?.data?.relationships?.[key]?.data?.id;
    if (id == null) return null;
    const record = (doc?.included || []).find(
      (r) => r.type === type && String(r.id) === String(id)
    );
    return { id: String(id), name: String(record?.attributes?.name ?? "") };
  };

  const currentRateTable = linked("rate-table", "rate-tables");
  const currentCurrency = linked("currency", "currencies");
  return {
    rateTableId: currentRateTable?.id ?? null,
    currencyId: currentCurrency?.id ?? null,
    currentRateTable,
    currentCurrency,
  };
};
